import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GitCompare, Sparkles, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { climateZoneForLat, zoneStyle } from "@/lib/climate";
import ProUpsell from "@/components/ProUpsell";

export default function CompareHives() {
  const navigate = useNavigate();
  const [hives, setHives] = useState([]);
  const [selected, setSelected] = useState([]); // hive ids
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/hives");
        setHives(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load hives");
      }
    })();
  }, []);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 4 ? prev : [...prev, id]
    );
  };

  const run = async () => {
    if (selected.length < 2) {
      toast.message("Pick at least 2 hives to compare");
      return;
    }
    setBusy(true);
    try {
      const r = await api.get("/trends/compare", { params: { hive_ids: selected.join(","), months: 12 } });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Failed to compare");
    } finally {
      setBusy(false);
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    return data.hives.map((h) => ({
      ...h.hive,
      totals: h.trends.totals,
      monthsWithData: h.trends.series.length,
    }));
  }, [data]);

  return (
    <div className="space-y-8" data-testid="compare-page">
      <header className="space-y-1">
        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Pro feature
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Compare hives</h1>
        <p className="text-stone-600">Pick 2–4 hives and see their inspections, harvests, treatments side-by-side.</p>
      </header>

      <section className="bg-white border border-stone-200 rounded-2xl p-6">
        <h2 className="font-display text-lg font-medium mb-3">Choose hives</h2>
        <div className="flex flex-wrap gap-2">
          {hives.map((h) => {
            const active = selected.includes(h.id);
            return (
              <button
                key={h.id}
                onClick={() => toggle(h.id)}
                disabled={!active && selected.length >= 4}
                className={`text-sm px-3 py-1.5 rounded-full border transition ${
                  active
                    ? "bg-amber-500 border-amber-600 text-stone-950 font-semibold"
                    : "border-stone-300 hover:border-stone-400 text-stone-700"
                }`}
                data-testid={`compare-select-${h.id}`}
              >
                {h.name}
              </button>
            );
          })}
          {hives.length === 0 && <div className="text-sm text-stone-500">No hives yet.</div>}
        </div>
        <div className="flex items-center justify-between mt-5 gap-3 flex-wrap">
          <div className="text-xs text-stone-500">{selected.length}/4 selected</div>
          <div className="flex items-center gap-2">
            {selected.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelected([])} data-testid="compare-clear-btn">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
            <Button
              onClick={run}
              disabled={busy || selected.length < 2}
              className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-full"
              data-testid="compare-run-btn"
            >
              <GitCompare className="w-4 h-4 mr-1.5" />
              {busy ? "Comparing…" : "Compare"}
            </Button>
          </div>
        </div>
      </section>

      {data && (
        <section className="bg-white border border-stone-200 rounded-2xl p-6 overflow-x-auto" data-testid="compare-results">
          <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-display text-lg font-medium">Last 12 months</h2>
            <p className="text-xs text-stone-500 inline-flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" />
              Tap any bar to open that hive's trends
            </p>
          </div>
          <table className="w-full text-sm" data-testid="compare-table">
            <thead>
              <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
                <th className="text-left py-2 font-medium">Metric</th>
                {rows.map((r) => (
                  <th key={r.id} className="text-right py-2 font-medium">{r.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="Location" cells={rows.map((r) => r.weather_location_label || r.location || "—")} />
              <Row
                label="Climate zone"
                cells={rows.map((r) => {
                  const z = climateZoneForLat(r.latitude);
                  const style = z ? zoneStyle(z) : null;
                  if (!style) return <span className="text-stone-400">—</span>;
                  return (
                    <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${style.chip}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden />
                      {style.label}
                    </span>
                  );
                })}
              />
              <Row label="Yard" cells={rows.map((r) => r.yard || "—")} />
              <Row label="Strength" cells={rows.map((r) => r.strength || "—")} />
              <BarRow label="Inspections" rows={rows} value={(r) => r.totals.inspections} navigate={navigate} accent="#0EA5E9" highlight />
              <BarRow label="Harvest (lb)" rows={rows} value={(r) => r.totals.harvests_lb} navigate={navigate} accent="#F59E0B" highlight />
              <BarRow label="Diagnoses" rows={rows} value={(r) => r.totals.diagnoses} navigate={navigate} accent="#EF4444" />
              <BarRow label="Treatments" rows={rows} value={(r) => r.totals.treatments} navigate={navigate} accent="#8B5CF6" />
              <BarRow label="Feedings" rows={rows} value={(r) => r.totals.feedings} navigate={navigate} accent="#10B981" />
              <Row label="Months with data" cells={rows.map((r) => r.monthsWithData)} />
            </tbody>
          </table>
        </section>
      )}

      <ProUpsell open={upsell} onOpenChange={setUpsell} feature="compare-hives" />
    </div>
  );
}

function Row({ label, cells, highlight }) {
  return (
    <tr className="border-b border-stone-100">
      <td className="py-2.5 text-stone-700">{label}</td>
      {cells.map((v, i) => (
        <td key={i} className={`py-2.5 text-right tabular-nums ${highlight ? "font-semibold text-stone-900" : "text-stone-700"}`}>
          {v ?? "—"}
        </td>
      ))}
    </tr>
  );
}

/**
 * BarRow renders a row where each cell shows a horizontal bar proportional to
 * the max value across all compared hives, plus the numeric total at the right.
 * Clicking a cell deep-links to that hive's HiveDetail Trends tab.
 */
function BarRow({ label, rows, value, navigate, accent, highlight }) {
  const values = rows.map((r) => Number(value(r)) || 0);
  const max = Math.max(1, ...values);
  return (
    <tr className="border-b border-stone-100">
      <td className="py-2.5 text-stone-700">{label}</td>
      {rows.map((r, i) => {
        const v = values[i];
        const pct = (v / max) * 100;
        const isMax = v > 0 && v === max && values.filter((x) => x === max).length === 1;
        return (
          <td key={r.id} className="py-2.5 align-middle">
            <button
              type="button"
              onClick={() => navigate(`/app/hives/${r.id}?tab=trends`)}
              className="group w-full min-w-[140px] flex items-center justify-end gap-2 rounded-md py-1 px-1.5 -mx-1.5 hover:bg-stone-50 transition focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid={`compare-bar-${label.toLowerCase().replace(/[^a-z]/g, "-")}-${r.id}`}
              title={`Open ${r.name}'s Trends tab`}
            >
              <span className="relative flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                <span
                  className="absolute inset-y-0 left-0 rounded-full transition-all"
                  style={{ width: `${pct}%`, background: accent, opacity: v > 0 ? 1 : 0.2 }}
                />
              </span>
              <span className={`tabular-nums shrink-0 min-w-[2.5rem] text-right text-xs ${highlight ? "font-semibold text-stone-900" : "text-stone-700"} ${isMax ? "underline decoration-2 underline-offset-2" : ""}`}>
                {Number.isInteger(v) ? v : v.toFixed(1)}
              </span>
              <ArrowUpRight className="w-3 h-3 text-stone-300 group-hover:text-stone-600 transition shrink-0" />
            </button>
          </td>
        );
      })}
    </tr>
  );
}
